import React, { useEffect, useState } from "react";
import { Button } from "reactstrap";
import { useTranslation } from "react-i18next";
import useApiCallService from "../../Common/Helpers/ApiCall";
import { FlashMessage } from "../../Common/Helpers/FlashMessage";
import SystemSettingRequestModel from "../Models/Request/SystemSettingRequestModel";
import { SlidePanel } from "../../Common/Components/Modal/SlidePanel";
import { CustomModal } from "../../Common/Components/Modal/CustomModal";
import { EconomicForm } from "./EconomicForm";
import { CheckUserPermission } from "../../AppSystem/Helpers/CheckUserPermission";
import { SettingsListHeader } from "../Components/SettingsListHeader";
import { EconomicSettingsAccordion } from "../Components/EconomicSettingsAccordion";

const STATIC_ECONOMIC_SETTINGS_MAP = [
    { id: 'HourlySalary', title: 'economic.timarvode', group: 'economic.arvoden' },
    // Mileage: total first ("Sammanlagd milersättning"), then its tax-free part ("Varav skattefri del")
    { id: 'PayableTravelPer10Km', title: 'economic.skattepliktig', group: 'economic.milersattningar' },
    { id: 'DeductableTravelPer10Km', title: 'economic.skattefri', group: 'economic.milersattningar' },
    { id: 'PayablePassangerTravelPer10Km', title: 'economic.passagerarersattning', group: 'economic.milersattningar' },
    { id: 'IncomeTax', title: 'economic.preliminarSkatt', group: 'economic.skatter' }
];

export default function AdminEconomic() {
    const { t, i18n } = useTranslation();

    // Swedish uses comma as decimal separator (e.g. 31,42 %)
    const formatDecimal = (value) => {
        const text = String(value ?? '');
        return (i18n.language || '').startsWith('se') ? text.replace('.', ',') : text;
    };
    const { ApiCall } = useApiCallService();
    const [settings, setSettings] = useState({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    // SlidePanel state
    const [formOpen, setFormOpen] = useState(false);
    const [editingItem, setEditingItem] = useState(null);

    // Delete confirmation (employer contribution tiers)
    const [deleteTierId, setDeleteTierId] = useState(null);

    let permissionCreate = false;
    if (CheckUserPermission("system.setting.economic.create") || CheckUserPermission("system.setting.economic") || CheckUserPermission("system.setting") || CheckUserPermission("system")) {
        permissionCreate = true;
    }

    let permissionUpdate = false;
    if (CheckUserPermission("system.setting.economic.update") || CheckUserPermission("system.setting.economic") || CheckUserPermission("system.setting") || CheckUserPermission("system")) {
        permissionUpdate = true;
    }

    let permissionDelete = false;
    if (CheckUserPermission("system.setting.economic.delete") || CheckUserPermission("system.setting.economic") || CheckUserPermission("system.setting") || CheckUserPermission("system")) {
        permissionDelete = true;
    }

    const fetchSettings = () => {
        setLoading(true);
        const options = {
            url: '/systemsetting/admin-list',
            method: 'GET'
        };

        const handleResponse = (response) => {
            if (response) {
                const dataArray = response.systemSettingRequests || response;
                const settingsObj = {};
                if (Array.isArray(dataArray)) {
                    dataArray.forEach(item => {
                        settingsObj[item.id] = item.value;
                    });
                }
                setSettings(settingsObj);
            }
            setLoading(false);
        };
        ApiCall(options, handleResponse);
    };

    useEffect(() => {
        fetchSettings();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleEdit = (item) => {
        setEditingItem({
            ...item,
            value: settings[item.id]
        });
        setFormOpen(true);
    };

    const handleSave = (id, value) => {
        setSaving(true);
        const requestModel = new SystemSettingRequestModel({ id, value: value?.toString() || "" });
        const isNew = settings[id] === undefined;
        
        const options = {
            url: '/systemsetting/admin',
            method: isNew ? 'POST' : 'PUT',
            data: requestModel
        };

        ApiCall(options, (res, err) => {
            if (!err) {
                FlashMessage(t('economic.updateSuccess'), 'flash-messageSuccess');
                setSettings(prev => ({ ...prev, [id]: value }));
                setFormOpen(false);
            } else {
                FlashMessage(t('economic.updateError'), 'flash-messageError');
                setFormOpen(false);
            }
            setSaving(false);
        });
    };

    const handleDeleteTier = (id) => {
        setLoading(true);
        const options = {
            url: `/systemsetting/admin?id=${id}`,
            method: 'DELETE'
        };
        ApiCall(options, (res, err) => {
            if (!err) {
                FlashMessage(t('economic.updateSuccess') || 'Success', 'flash-messageSuccess');
                setSettings(prev => {
                    const newSettings = { ...prev };
                    delete newSettings[id];
                    return newSettings;
                });
                setFormOpen(false);
            } else {
                FlashMessage(t('economic.updateError') || 'Error', 'flash-messageError');
            }
            setLoading(false);
        });
    };

    const handleAddTier = () => {
        const existingTiers = Object.keys(settings)
            .filter(key => key.startsWith('EmployerContribution_Tier_'))
            .map(key => parseInt(key.replace('EmployerContribution_Tier_', ''), 10) || 0);
        
        let nextTierNum = 1;
        while (existingTiers.includes(nextTierNum)) {
            nextTierNum++;
        }

        const maxPriority = Object.keys(settings)
            .filter(key => key.startsWith('EmployerContribution_Tier_'))
            .reduce((max, key) => {
                try {
                    const p = JSON.parse(settings[key]).Priority || 0;
                    return p > max ? p : max;
                } catch(e) { return max; }
            }, 0);

        const newTierId = `EmployerContribution_Tier_${nextTierNum}`;
        
        setEditingItem({
            id: newTierId,
            title: `economic.employerContributionTier${nextTierNum}`,
            value: JSON.stringify({ FromYear: '', ToYear: '', Rate: '', Priority: maxPriority + 1 })
        });
        setFormOpen(true);
    };

    const handleMoveTier = async (itemId, direction) => {
        const tiersOnly = dynamicTiers.filter(r => r.id !== 'EmployerContribution_Default');
        const index = tiersOnly.findIndex(t => t.id === itemId);
        if (index === -1) return;
        
        const newIndex = direction === 'up' ? index - 1 : index + 1;
        if (newIndex < 0 || newIndex >= tiersOnly.length) return;
        
        const itemA = tiersOnly[index];
        const itemB = tiersOnly[newIndex];
        
        try {
            const parsedA = JSON.parse(settings[itemA.id] || '{}');
            const parsedB = JSON.parse(settings[itemB.id] || '{}');
            
            // Swap Priority
            const tempPriority = parsedA.Priority;
            parsedA.Priority = parsedB.Priority;
            parsedB.Priority = tempPriority;
            
            const newValueA = JSON.stringify(parsedA);
            const newValueB = JSON.stringify(parsedB);
            
            setLoading(true);
            
            const updateItem = (id, value, title) => {
                return new Promise((resolve) => {
                    const options = {
                        url: '/systemsetting/admin',
                        method: 'PUT',
                        data: {
                            id: id,
                            value: value,
                            description: title,
                            group: 'economic.skatter'
                        }
                    };
                    ApiCall(options, (res, err) => {
                        resolve({ res, err });
                    });
                });
            };
            
            const results = await Promise.all([
                updateItem(itemA.id, newValueA, itemA.title),
                updateItem(itemB.id, newValueB, itemB.title)
            ]);
            
            const hasError = results.some(r => r.err);
            if (!hasError) {
                FlashMessage(t('validationMessage.orderUpdated') || 'Order updated', 'flash-messageSuccess');
            } else {
                FlashMessage(t('validationMessage.updateError') || 'Error updating order', 'flash-messageError');
            }
            fetchSettings();
        } catch (e) {
            console.error("Error moving tier", e);
            FlashMessage(t('validationMessage.sendError'), 'flash-messageError');
            setLoading(false);
        }
    };

    if (loading) {
        return <div className="p-5 text-center"><i className="bx bx-loader bx-spin font-size-24"></i></div>;
    }

    // Dynamic tiers computation
    const dynamicTiers = Object.keys(settings)
        .filter(key => key.startsWith('EmployerContribution_Tier_'))
        .sort((a, b) => {
            let pA = 9999, pB = 9999;
            try { pA = JSON.parse(settings[a]).Priority ?? 9999; } catch(e){}
            try { pB = JSON.parse(settings[b]).Priority ?? 9999; } catch(e){}
            if (pA !== pB) return pA - pB;

            const numA = parseInt(a.replace('EmployerContribution_Tier_', ''), 10) || 0;
            const numB = parseInt(b.replace('EmployerContribution_Tier_', ''), 10) || 0;
            return numA - numB;
        })
        .map(key => ({
            id: key,
            title: `economic.employerContributionTier${key.replace('EmployerContribution_Tier_', '')}`,
            group: 'economic.arbetsgivaravgifter'
        }));

    if (settings['EmployerContribution_Default']) {
        dynamicTiers.push({
            id: 'EmployerContribution_Default',
            title: 'economic.employerContributionDefault',
            group: 'economic.arbetsgivaravgifter'
        });
    }

    // Helper to format values
    const renderValue = (rowItem) => {
        const val = settings[rowItem.id];
        if (rowItem.id === 'EmploymentTax') {
            return <span className="badge bg-light text-secondary border px-2 py-1">{t('economic.configured') || 'Configured'}</span>;
        }
        if (rowItem.id === 'IncomeTax' && val) {
            return `${formatDecimal(val)} %`;
        }
        if ((rowItem.group === 'economic.milersattningar' || rowItem.group === 'economic.arvoden') && val) {
            return `${formatDecimal(val)} kr`;
        }
        return val ? formatDecimal(val) : '-';
    };

    const renderEditButton = (rowItem) => (
        permissionUpdate ? (
            <Button
                className="bg-green-light text-green border-0 position-relative"
                onClick={() => handleEdit(rowItem)}
            >
                <i className="bx bxs-pencil fs-5 align-middle pb-1" />
                <div className="tooltip-custom-button">{t("button.edit")}</div>
            </Button>
        ) : null
    );

    const staticRows = (groupKey) => STATIC_ECONOMIC_SETTINGS_MAP
        .filter(i => i.group === groupKey)
        .map(item => ({ ...item, label: t(item.title), value: renderValue(item) }));

    const tiersOnlyCount = dynamicTiers.filter(r => r.id !== 'EmployerContribution_Default').length;

    const employerContributionRows = dynamicTiers.map(rowItem => {
        const isDefault = rowItem.id === 'EmployerContribution_Default';
        let birthYearLabel = "-";
        let rate = "0 %";
        try {
            const parsed = JSON.parse(settings[rowItem.id] || '{}');
            birthYearLabel = `${parsed.FromYear || '-'} - ${parsed.ToYear || '-'}`;
            rate = `${formatDecimal(parsed.Rate || '0')} %`;
        } catch (e) {}

        return {
            ...rowItem,
            isDefault,
            label: isDefault
                ? <><span className="fw-semibold">{t('economic.defaultSettingTitle')}</span> <span className="text-muted">{t('economic.defaultSettingSubtitle')}</span></>
                : birthYearLabel,
            value: rate,
        };
    });

    const renderTierActions = (rowItem, index) => {
        if (rowItem.isDefault) return renderEditButton(rowItem);
        if (!permissionUpdate && !permissionDelete) return null;
        return (
            <>
                {renderEditButton(rowItem)}
                {permissionUpdate && (
                    <Button
                        className="bg-blue-light text-blue border-0 position-relative"
                        onClick={() => handleMoveTier(rowItem.id, 'up')}
                        disabled={index === 0}
                    >
                        <i className="bx bx-up-arrow-alt fs-5 align-middle pb-1" />
                        <div className="tooltip-custom-button">{t("button.moveUp")}</div>
                    </Button>
                )}
                {permissionUpdate && (
                    <Button
                        className="bg-blue-light text-blue border-0 position-relative"
                        onClick={() => handleMoveTier(rowItem.id, 'down')}
                        disabled={index === tiersOnlyCount - 1}
                    >
                        <i className="bx bx-down-arrow-alt fs-5 align-middle pb-1" />
                        <div className="tooltip-custom-button">{t("button.moveDown")}</div>
                    </Button>
                )}
                {permissionDelete && (
                    <Button
                        className="box-red border-0 position-relative"
                        onClick={() => setDeleteTierId(rowItem.id)}
                    >
                        <i className="bx bx-x-circle fs-5 align-middle pb-1" />
                        <div className="tooltip-custom-button">{t("button.delete")}</div>
                    </Button>
                )}
            </>
        );
    };

    const deleteTierLabel = employerContributionRows.find(r => r.id === deleteTierId)?.label;

    return (
        <div className="mb-5 p-0 position-relative sl-wrap economic-settings">
            <EconomicSettingsAccordion
                title={t('economic.arvoden')}
                labelHeader={t('modelLabel.type')}
                valueHeader={t('economic.belopp')}
                rows={staticRows('economic.arvoden')}
                renderActions={renderEditButton}
            />
            <EconomicSettingsAccordion
                title={t('economic.milersattningar')}
                labelHeader={t('modelLabel.type')}
                valueHeader={t('economic.beloppPerMil')}
                rows={staticRows('economic.milersattningar')}
                renderActions={renderEditButton}
            />
            <EconomicSettingsAccordion
                title={t('economic.skatter')}
                labelHeader={t('modelLabel.type')}
                valueHeader={t('economic.sats')}
                rows={staticRows('economic.skatter')}
                renderActions={renderEditButton}
            />
            <EconomicSettingsAccordion
                title={t('economic.arbetsgivaravgifter')}
                labelHeader={`${t('economic.birthYearFrom')} - ${t('economic.birthYearTo').toLowerCase()}`}
                valueHeader={t('economic.avgift')}
                rows={employerContributionRows}
                renderActions={renderTierActions}
                toolbar={
                    <SettingsListHeader
                        createText={t('economic.addYearRow')}
                        showCreate={permissionCreate}
                        onCreate={handleAddTier}
                        mobileFixed={false}
                    />
                }
            />

            <CustomModal
                isOpen={deleteTierId !== null}
                toggle={() => setDeleteTierId(null)}
                body={
                    <div>
                        {t('validationMessage.modalDelete')}
                        {deleteTierLabel && <p className="mt-2 text-muted mb-0"><strong>{deleteTierLabel}</strong></p>}
                    </div>
                }
                footer={
                    <>
                        <Button className="box-green border-0" onClick={() => { const id = deleteTierId; setDeleteTierId(null); handleDeleteTier(id); }}>
                            {t('button.delete')}
                        </Button>
                        <Button className="box-red border-0" onClick={() => setDeleteTierId(null)}>
                            {t('button.cancel')}
                        </Button>
                    </>
                }
            />

            <SlidePanel
                isOpen={formOpen}
                onClose={() => setFormOpen(false)}
                title={editingItem
                    ? (editingItem.id.startsWith('EmployerContribution') ? t('economic.arbetsgivaravgiftPercent') : t(editingItem.title))
                    : t('button.edit')}
                variant="center"
                zIndexOffset={20}
            >
                {editingItem && (
                    <EconomicForm
                        initial={editingItem}
                        onSave={handleSave}
                        onCancel={() => setFormOpen(false)}
                        saving={saving}
                        settings={settings}
                    />
                )}
            </SlidePanel>
        </div>
    );
}
